import { useMemo } from "react";
import { useCreateExpenseMutation } from "web_myplann/Api/Finances/useCreateExpenseMutation";
import {
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { useInspirationBaseline } from "web_myplann/Inspiration/Hooks/useInspirationBaseline";
import { useInspirationJourney } from "web_myplann/Inspiration/Hooks/useInspirationJourney";
import { useRetirementPortfolioItems } from "web_myplann/Inspiration/Retirement/Hooks/useRetirementPortfolioItems";
import type {
	RetirementInputs,
	RetirementLoanFormModel,
} from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { computeRetirementContext } from "web_myplann/Inspiration/Retirement/Utils/retirementMath";
import { createId } from "web_myplann/Shared/Utils/createId";

export type RetirementSheet = "expenses" | "addLoan" | "statePension";

export const useRetirementJourney = () => {
	const {
		age,
		birthDate,
		income,
		monthlyExpenses,
		freeCashflow,
		isLoaded: isBaselineLoaded,
	} = useInspirationBaseline();
	const { items: portfolioItems, isLoaded: isPortfolioLoaded } =
		useRetirementPortfolioItems();
	const { mutate: createExpense } = useCreateExpenseMutation();
	const { inputs, patchInputs, submitInputs, closeSheet, ...journey } =
		useInspirationJourney<RetirementInputs, RetirementSheet>({});

	const context = useMemo(
		() =>
			computeRetirementContext(inputs, {
				age,
				birthDate,
				income,
				monthlyExpenses,
				freeCashflow,
				portfolioItems,
			}),
		[
			inputs,
			age,
			birthDate,
			income,
			monthlyExpenses,
			freeCashflow,
			portfolioItems,
		],
	);

	const toggleDrop = (id: string) => {
		const dropsOff = inputs.dropsOff ?? [];
		patchInputs({
			dropsOff: dropsOff.includes(id)
				? dropsOff.filter((dropId) => dropId !== id)
				: [...dropsOff, id],
		});
	};

	const addManualDrop = (drop: RetirementLoanFormModel) =>
		submitInputs({
			extraDrops: [...(inputs.extraDrops ?? []), { id: createId(), ...drop }],
		});

	const removeManualDrop = (id: string) =>
		patchInputs({
			extraDrops: (inputs.extraDrops ?? []).filter((drop) => drop.id !== id),
			dropsOff: (inputs.dropsOff ?? []).filter((dropId) => dropId !== id),
		});

	const saveExpenses = (monthlyPayment: number) => {
		closeSheet();
		createExpense({
			name: ClientFinancialPlanExpenseNameEnum.Other,
			type: ClientFinancialPlanExpenseTypeEnum.Other,
			monthlyPayment,
			sustainableAmount: 0,
		});
	};

	return {
		...journey,
		closeSheet,
		patchInputs,
		submitInputs,
		toggleDrop,
		addManualDrop,
		removeManualDrop,
		saveExpenses,
		context,
		isLoaded: isBaselineLoaded && isPortfolioLoaded,
	};
};
